import {
    Component, Mixins,
} from 'vue-property-decorator';
import moment from 'moment-jalaali';
import VuePersianDatetimePicker from 'vue-persian-datetime-picker';
import { v4 as uuid } from 'uuid';
import { digits } from '@/nodes/helpers/number';
import FormsMixin from '@components/shared/mixins/forms/index';
import YInput from '@components/Input';
import Hybrid from '@components/shared/mixins/hybrid';
import Classes from '@components/shared/mixins/classes';
import config from '@components/shared/config';
import Props from './props';

/**
 * @name DatePicker
 */
@Component({
    components: {
        datePicker: VuePersianDatetimePicker,
        YInput,
    },
})
class YDatePicker extends Mixins(FormsMixin, Hybrid, Props, Classes) {
    modelType = 'number';
    defaultCalendar: Array<string> = this.$i18n.locale === 'fa' ? ['fa'] : ['en'];

    formats = {
        fa: {
            date    : 'DD MMMM YYYY',
            time    : 'HH:mm',
            datetime: 'DD MMMM YYYY - HH:mm',
        },
        en: {
            date    : 'MMMM DD, YYYY',
            time    : 'HH:mm',
            datetime: 'MMMM DD, YYYY - HH:mm',
        },
    }

    addons = {
        iconPrefix: {
            slug: 'far',
        },
        date: {
            icon : 'calendar-alt',
            title: this.$t('components.date_picker.addon.date'),
        },
        time: {
            icon : 'clock',
            title: this.$t('components.date_picker.addon.time'),
        },
        datetime: {
            icon : 'calendar-alt',
            title: this.$t('components.date_picker.addon.datetime'),
        },
        year: {
            icon : 'calendar',
            title: this.$t('components.date_picker.addon.year'),
        },
        month: {
            icon : 'calendar-week',
            title: this.$t('components.date_picker.addon.month'),
        },
    }

    /**
     * UUID
     */
    keyHelper: string = uuid();

    /**
     * Props list that active by developer
     */
    activeCommonProps: Array<string> = [];

    /**
     * The css parent class of the component
     */
    parentClass: string = this.componentClass(this.baseClass, config.prefix.datePicker);

    /**
     * Create Common Props for the datePicker.
     * 
     * @returns {object} - List of props
     */
    get commonProps(): Record<string, any> {
        const commonProps: Record<string, any> = {
            baseClass: this.baseClass,
        };
        this.activeCommonProps.forEach((item) => {
            if (this.element && this.element[item]) {
                commonProps[item] = this.element[item];
            } else {
                commonProps[item] = this[item];
            }
        });
        return commonProps;
    }

    /**
     * Create classes array for the datePicker.
     * 
     * @returns {Array} - Array of classes
     */
    get classes(): Array<string> {
        const cls: Array<string> = [];
        cls.push(...this.commonClasses('DatePicker', this.commonProps, this.baseClass || config.prefix.datePicker));
        return cls;
    }

    /**
     * Return locales of date picker
     * 
     * @returns {Array} - Array of locales
     */
    get calendarOptions(): Array<string> {
        if (!this.calendar) {
            return this.defaultCalendar;
        }
        return this.calendar.map((el) => (el === 'jalaali' ? 'fa' : 'en')) || this.defaultCalendar;
    }

    /**
     * Return date value parsed with format
     *
     * @param {*} date
     * @returns {*}
     */
    parseDateValue(date: any[]): any {
        if (!date) {
            return '';
        }

        const value = digits(date);
        const { locale } = this.$i18n;

        let format = this.getValue('format') || this.formats[locale][this.getValue('type')];
        if (this.calendarOptions[0] === 'fa') {
            format = format.replace(/([Y|M|D])\1{0,}/g, (w) => `j${w}`);
        }
        return this.getBoolean('multiple')
            ? date.map((d) => digits(moment(d, 'X').locale(locale).format(format), locale)).join(this.getValue('separator'))
            : digits(moment(value, 'X').locale(locale).format(format), locale);
    }
}

export default YDatePicker;
